// Path: src/pages/PrivacyPolicy.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

export default function PrivacyPolicy() {
  return (
    <section className="px-6 md:px-12 py-10 max-w-4xl mx-auto leading-relaxed text-gray-800">
      <Helmet>
        <title>Privacy Policy | Shivansh Mahasabha</title>
        <meta
          name="description"
          content="Read how Shivansh Mahasabha collects, uses, and protects your data. Includes secure database storage and Razorpay payment processing details."
        />
        <link rel="canonical" href="https://www.shivanshmahasabha.in/privacy-policy" />
      </Helmet>

      <h1 className="text-3xl font-bold text-[#204993] mb-6">Privacy Policy</h1>

      <p className="mb-4">
        Shivansh Mahasabha (“we”, “our”, “us”) is committed to protecting the privacy of our
        members, donors, and visitors. This Privacy Policy explains what we collect, how we use it,
        how we keep it secure, and your choices—especially in relation to online payments processed
        by Razorpay.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">1) Scope & Consent</h2>
      <p className="mb-4">
        By using our website, submitting a membership form, subscribing to newsletters, or making a
        donation, you agree to this Privacy Policy and our handling of data as described below.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">2) Information We Collect</h2>
      <ul className="list-disc ml-6 mb-4">
        <li>
          <strong>Provided by you:</strong> name, phone, Aadhaar (for verification), city, state,
          email, and any message you submit.
        </li>
        <li>
          <strong>Donation/payment metadata:</strong> amount, currency, order ID, payment ID,
          status, method (e.g., card/UPI/netbanking), timestamps, and related records.
        </li>
        <li>
          <strong>Technical:</strong> device/browser info, IP address, pages viewed, and basic
          analytics (cookies may be used; see Cookies & Analytics below).
        </li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">3) How We Use Your Information</h2>
      <ul className="list-disc ml-6 mb-4">
        <li>To process memberships, donations, receipts, and acknowledgements.</li>
        <li>To communicate updates, newsletters, impact stories, and organisational notices.</li>
        <li>To comply with legal, taxation, and audit requirements.</li>
        <li>To improve the website’s security, performance, and user experience.</li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">4) Payments Processed via Razorpay</h2>
      <p className="mb-3">
        We use <strong>Razorpay Software Private Limited (“Razorpay”)</strong> as our payment
        service provider. When you donate, we share necessary details with Razorpay to create and
        verify your order and payment (e.g., name, contact, amount, order ID). <strong>Card/UPI/netbanking
        credentials are collected and processed on Razorpay’s secure systems</strong> and are not
        stored on our servers.
      </p>
      <ul className="list-disc ml-6 mb-4">
        <li>Razorpay is <strong>PCI DSS compliant</strong> and follows industry-standard security.</li>
        <li>
          We may receive non-sensitive payment information from Razorpay (payment ID, method,
          status) for reconciliation, receipts, refunds, and dispute resolution.
        </li>
        <li>
          For more details, please refer to Razorpay’s{" "}
          <a
            href="https://razorpay.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f9b806] underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://razorpay.com/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f9b806] underline"
          >
            Terms of Use
          </a>
          .
        </li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">5) Refunds, Cancellations & Chargebacks</h2>
      <p className="mb-4">
        Donations are generally non-refundable. However, in genuine cases (duplicate transactions,
        technical errors), you can email{" "}
        <a href="mailto:shivanshmahasabha@gmail.com" className="text-[#f9b806] underline">
          shivanshmahasabha@gmail.com
        </a>{" "}
        within 7 days with your payment reference (order ID/payment ID/amount/date). Approved
        refunds are processed via Razorpay to the original payment method. For bank/card disputes
        or chargebacks, we may share necessary records with Razorpay and/or your bank to resolve the
        case.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">6) Secure Database Storage</h2>
      <p className="mb-4">
        We store membership, donation, and communication data in{" "}
        <strong>encrypted cloud databases (e.g., MongoDB Atlas)</strong> with role-based access.
        Transport is secured via <strong>HTTPS/TLS</strong>; data at rest is protected with
        provider-level encryption. Access is restricted to authorised personnel only.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">7) Data Retention</h2>
      <ul className="list-disc ml-6 mb-4">
        <li>
          Donation and finance records: retained as required by law and audit/tax regulations.
        </li>
        <li>Membership records: for the duration of membership and a reasonable period thereafter.</li>
        <li>Operational logs/analytics: typically for 12–24 months, unless needed for security/legal reasons.</li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">8) Data Sharing & Processors</h2>
      <p className="mb-3">
        We do not sell or rent personal data. We share information only with trusted processors to
        operate our services, for example:
      </p>
      <ul className="list-disc ml-6 mb-4">
        <li>Payment processing: Razorpay.</li>
        <li>Hosting/CDN/build tools (e.g., Vercel or similar) to serve the website.</li>
        <li>Cloud database and storage providers (e.g., MongoDB Atlas or similar).</li>
        <li>Email and communication tools for receipts, newsletters, and support.</li>
        <li>Regulators, auditors, law enforcement, when legally required.</li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">9) Security Measures</h2>
      <ul className="list-disc ml-6 mb-4">
        <li>HTTPS across the site; HSTS enabled where supported.</li>
        <li>Encryption at rest (provider-managed) and in transit.</li>
        <li>Principle of least privilege; role-based access; periodic reviews.</li>
        <li>Dependency and OS patching aligned with provider best practices.</li>
      </ul>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">10) Cookies & Analytics</h2>
      <p className="mb-4">
        We may use essential cookies for site functionality and limited analytics to understand usage
        (e.g., page views, device types). You can control cookies via your browser settings. Blocking
        some cookies may impact certain features.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">11) Children’s Privacy</h2>
      <p className="mb-4">
        Our services are intended for adults. If you believe a child under applicable age has
        provided personal data, please contact us so we can delete it promptly.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">12) International Transfers</h2>
      <p className="mb-4">
        Our service providers may store or process data outside your country. We select reputable
        providers and require appropriate safeguards consistent with applicable laws.
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">13) Your Rights</h2>
      <p className="mb-4">
        You can request access, correction, update, or deletion of your personal information (subject
        to legal/audit retention) by contacting us at{" "}
        <a href="mailto:shivanshmahasabha@gmail.com" className="text-[#f9b806] underline">
          shivanshmahasabha@gmail.com
        </a>
        .
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">14) Contact & Grievance</h2>
      <p className="mb-2">Address: 90 Tagore Villa, Dehradun, Uttarakhand, India</p>
      <p className="mb-2">Phone: +91 78953 45056</p>
      <p className="mb-4">
        Email:{" "}
        <a href="mailto:shivanshmahasabha@gmail.com" className="text-[#f9b806] underline">
          shivanshmahasabha@gmail.com
        </a>
      </p>

      <h2 className="text-xl font-semibold text-[#204993] mt-8 mb-3">15) Developer Information</h2>
      <p className="mb-4">
        This website was designed and developed by <strong>Panjal Rawat</strong> (Freelance Web
        Developer). For inquiries, contact:{" "}
        <a href="mailto:panjalrawat.dev@gmail.com" className="text-[#f9b806] underline">
          pranjalr842@gmail.com
        </a>
        .
      </p>

      <p className="text-sm mt-8 opacity-70">Effective Date: 22 August 2025</p>
    </section>
  );
}
